'use client';

import { FC, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass, faXmark } from '@fortawesome/pro-solid-svg-icons';
import { useRouter, usePathname } from 'core/i18n/navigation';
import { useActiveSpace } from 'xstate/hooks/useActiveSpace';
import { useSearchState } from 'xstate/hooks/useSearchState';
import {
  submitSearch,
  clearSearch,
  loadRecentSearches,
} from 'xstate/actors/SearchActor';
import { buildSpacePath } from 'config/routing';
import {
  SearchScopeFilters,
  useSearchScopeLabel,
} from 'modules/Search/SearchScopeFilters';
import { RecentSearches } from 'modules/Search/RecentSearches';
import { useTranslations } from 'next-intl';

// The dashboard's centre search box (desktop, lg+). Focusing it opens a palette
// with the current scope, box/collection filters and recent searches. Typing +
// Enter (or picking a recent) commits the query to the shared SearchActor and
// navigates to the space's Search tab. Hidden on the search screen itself,
// which has its own input bound to the same state.
const HeaderSearch: FC = () => {
  const t = useTranslations();
  const { selectedSpace } = useActiveSpace();
  const router = useRouter();
  const pathname = usePathname();
  const { query } = useSearchState();
  const scopeLabel = useSearchScopeLabel();

  const [text, setText] = useState(query);
  const [open, setOpen] = useState(false);
  const fieldRef = useRef<HTMLDivElement>(null);

  // Reflect the committed query (e.g. entered on the search screen) into the
  // field whenever it changes.
  useEffect(() => setText(query), [query]);

  // Load persisted recent searches on mount (client-only).
  useEffect(() => loadRecentSearches(), []);

  // Close on outside click. We can't use a simple click-away wrapper because
  // the filter dropdowns render in a Headless UI portal (outside this node);
  // treat clicks inside any Headless UI floating element as "inside".
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (fieldRef.current?.contains(target)) return;
      if (target.closest('[id^="headlessui-"]')) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open]);

  if (pathname.endsWith('/search')) return null;

  const runSearch = (raw: string) => {
    const trimmed = raw.trim();
    submitSearch(trimmed);
    setText(trimmed);
    setOpen(false);
    if (selectedSpace) {
      router.push(
        `${buildSpacePath({ kind: 'search', spaceId: selectedSpace.id })}${
          trimmed ? `?q=${encodeURIComponent(trimmed)}` : ''
        }`,
      );
    }
  };

  const onClear = () => {
    setText('');
    clearSearch();
  };

  return (
    <div className={styles.wrap}>
      <div ref={fieldRef} className={styles.field}>
        <div className={styles.inputRow}>
          <FontAwesomeIcon
            icon={faMagnifyingGlass}
            className={classNames(
              styles.icon,
              open ? 'text-ink-tertiary' : 'text-white/80',
            )}
            aria-hidden="true"
          />
          <input
            value={text}
            onChange={(e) => setText(e.target.value)}
            onFocus={() => setOpen(true)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                runSearch(text);
              }
              // Escape closes the palette and leaves the field.
              if (e.key === 'Escape') {
                setOpen(false);
                e.currentTarget.blur();
              }
            }}
            placeholder={t('search.headerPlaceholder')}
            aria-label={t('search.title')}
            className={classNames(
              styles.input,
              open ? styles.inputOpen : styles.inputIdle,
            )}
          />
          {(text || open) && (
            <button
              type="button"
              onClick={onClear}
              aria-label={t('search.clear')}
              className={classNames(
                styles.clear,
                open ? styles.clearOpen : styles.clearIdle,
              )}
            >
              <FontAwesomeIcon icon={faXmark} />
            </button>
          )}
        </div>

        {open && (
          <div className={styles.panel}>
            {/* While typing, echo the term back with the Enter key, so it's
                clear a search runs on Enter (or a click), not as you type. */}
            {text.trim() && (
              <div className={styles.section}>
                <button
                  type="button"
                  onClick={() => runSearch(text)}
                  className={styles.action}
                >
                  <span className={styles.actionIcon}>
                    <FontAwesomeIcon
                      icon={faMagnifyingGlass}
                      aria-hidden="true"
                    />
                  </span>
                  <span className={styles.actionLabel}>
                    {t('search.searchFor', { query: text.trim() })}
                  </span>
                  <kbd className={styles.kbd}>{t('search.enterKey')}</kbd>
                </button>
              </div>
            )}

            <div className={styles.filters}>
              <SearchScopeFilters withLabel />
            </div>

            <div className={styles.recents}>
              <RecentSearches onPick={runSearch} />
            </div>

            <div className={styles.footer}>
              <span className="truncate">{scopeLabel}</span>
              <span className="flex-none">{t('search.paletteHint')}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default HeaderSearch;

const styles = {
  // A fixed-size slot between the two flexible sides of the header.
  wrap: 'hidden lg:flex flex-none w-full max-w-md px-6',
  field: 'relative w-full max-w-md',
  inputRow: 'relative',
  icon: 'pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-sm transition-colors',
  // Idle: a frosted pill that sits quietly on the blue header. Open: the
  // same surface as the palette below, so the two read as one.
  input: classNames(
    'w-full rounded-full border py-2 pl-10 pr-10 text-sm transition-colors duration-150',
    'focus:outline-none focus:ring-0',
  ),
  inputIdle: classNames(
    'border-white/20 bg-white/15 text-white placeholder:text-white/80',
    'hover:border-white/30 hover:bg-white/20',
  ),
  inputOpen:
    'border-transparent bg-surface-overlay text-ink-primary placeholder:text-ink-tertiary shadow-xs',
  clear: 'absolute right-4 top-1/2 -translate-y-1/2 transition-colors',
  clearOpen: 'text-ink-tertiary hover:text-ink-primary',
  clearIdle: 'text-white/70 hover:text-white',
  // The look of the composer's type picker: an overlay surface with a
  // hairline ring, sections split by hairlines.
  panel: classNames(
    'absolute left-0 right-0 top-full z-40 mt-1.5 overflow-hidden rounded-2xl',
    'bg-surface-overlay shadow-lg ring-1 ring-hairline divide-y divide-hairline',
  ),
  section: 'p-1.5',
  action: classNames(
    'flex w-full items-center gap-3 rounded-[10px] border border-accent-tint-ink/20 bg-accent-tint',
    'px-2 py-1.5 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-ink/40',
  ),
  actionIcon:
    'flex size-7 flex-none items-center justify-center rounded-lg bg-accent-fill text-[12px] text-white',
  actionLabel: 'min-w-0 flex-1 truncate text-sm font-semibold text-ink-primary',
  kbd: classNames(
    'flex-none rounded-md border border-stroke bg-surface-overlay px-1.5 py-0.5',
    'font-sans text-[11px] font-medium text-ink-secondary',
  ),
  filters: 'px-3.5 py-3',
  recents: 'p-1.5 empty:hidden',
  footer:
    'flex items-center justify-between gap-3 px-3.5 py-2 text-[11px] text-ink-tertiary',
};
